import { openDB, type DBSchema } from "idb";
import type { LinkServiceEnvironment } from "../../link-protocol";

const DATABASE_NAME = "quotation-link-records";
const DATABASE_VERSION = 1;
const STORE_NAME = "links";

export interface LocalLinkRecord {
  id: string;
  status: "active" | "revoked";
  shareUrl: string;
  accessToken: string;
  revokeToken: string;
  decryptionKey: string;
  displayName: string;
  fileCount: number;
  ciphertextBytes: number;
  ciphertextSha256: string;
  createdAt: string;
  expiresAt: string;
  environment: LinkServiceEnvironment;
}

export interface NewLocalLinkRecord extends Omit<
  LocalLinkRecord,
  "id" | "status" | "createdAt"
> {
  createdAt?: string;
}

interface LinkRecordDatabase extends DBSchema {
  links: {
    key: string;
    value: LocalLinkRecord;
    indexes: {
      "by-created-at": string;
    };
  };
}

async function openLinkDatabase() {
  return openDB<LinkRecordDatabase>(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(database) {
      const store = database.createObjectStore(STORE_NAME, {
        keyPath: "id",
      });
      store.createIndex("by-created-at", "createdAt");
    },
  });
}

export async function saveLocalLinkRecord(
  value: NewLocalLinkRecord,
): Promise<LocalLinkRecord> {
  const record: LocalLinkRecord = {
    ...value,
    id: crypto.randomUUID(),
    status: "active",
    createdAt: value.createdAt ?? new Date().toISOString(),
  };
  const database = await openLinkDatabase();
  try {
    await database.put(STORE_NAME, record);
  } finally {
    database.close();
  }
  return record;
}

export async function listLocalLinkRecords(): Promise<LocalLinkRecord[]> {
  const database = await openLinkDatabase();
  try {
    const records = await database.getAllFromIndex(STORE_NAME, "by-created-at");
    return records.toReversed();
  } finally {
    database.close();
  }
}

export async function markLocalLinkRevoked(
  id: string,
): Promise<LocalLinkRecord | null> {
  const database = await openLinkDatabase();
  try {
    const record = await database.get(STORE_NAME, id);
    if (record === undefined) {
      return null;
    }
    const updated: LocalLinkRecord = { ...record, status: "revoked" };
    await database.put(STORE_NAME, updated);
    return updated;
  } finally {
    database.close();
  }
}

export async function deleteLocalLinkRecord(id: string): Promise<void> {
  const database = await openLinkDatabase();
  try {
    await database.delete(STORE_NAME, id);
  } finally {
    database.close();
  }
}
